import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PaintingFrame } from '../components/PaintingFrame';
import ShareMenu from '../components/ShareMenu';
import { galleryItems as _seed, slugify, getDisplaySize } from '../mockData';
import { useGallery } from '../lib/usePublicData';
import { useT, localize } from '../lib/i18n';

const Gallery = () => {
  const { t, lang } = useT();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [lightbox, setLightbox] = useState(null);
  const { items: galleryItems } = useGallery();

  const featured = galleryItems.filter((i) => i.featured);

  // Filter chips are derived from the actual featured-item categories so they
  // always reflect what's truly browsable — never an empty filter.
  const rawCategories = Array.from(new Set(featured.map((i) => i.category).filter(Boolean))).sort();
  const categories = ['All', ...rawCategories];

  // Map English category values to translation keys for the chip labels.
  const labelForCategory = (cat) => {
    if (cat === 'All') return t('common.all');
    const key = {
      'Painting': 'gallery.filterPainting',
      'Sculpture': 'gallery.filterSculpture',
      'Mixed Media': 'gallery.filterMixedMedia',
      'Antique': 'gallery.filterAntique',
      'Drawing': 'gallery.filterDrawing',
    }[cat];
    return key ? t(key) : cat;
  };

  const filteredItems = featured.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          item.artist.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-cream py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="w-3 h-3 rotate-45 bg-gold mx-auto mb-6 border-2 border-dark-crimson"></div>
          <h1 className="text-7xl font-bold text-dark-green mb-6" style={{ fontFamily: 'Playfair Display, serif', letterSpacing: '0.1em' }}>
            {t('gallery.title')}
          </h1>
          <div className="h-1 w-40 bg-dark-crimson mx-auto mt-6 mb-8"></div>
          <p className="text-charcoal text-xl max-w-2xl mx-auto" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('gallery.subtitle')}
          </p>
        </div>

        {/* Search and Filter */}
        <div className="mb-16 space-y-8">
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-charcoal/60" size={20} />
            <Input
              type="text"
              placeholder={t('common.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 py-6 bg-ivory border-2 border-gold/40 text-dark-green rounded-none focus:border-dark-crimson transition-colors"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem' }}
            />
          </div>

          <div className="flex flex-wrap justify-center gap-4" data-testid="gallery-filter-chips">
            {categories.map((category) => (
              <Button
                key={category}
                data-testid={`gallery-filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setSelectedCategory(category)}
                className={`px-10 py-6 rounded-none border-2 transition-all duration-300 font-semibold tracking-wider ${
                  selectedCategory === category
                    ? 'bg-dark-crimson border-gold text-cream'
                    : 'bg-transparent border-rich-green text-dark-green hover:border-gold hover:bg-gold/10'
                }`}
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {labelForCategory(category)}
              </Button>
            ))}
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
          {filteredItems.map((item) => (
            <Card key={item.id} className="bg-ivory border-2 border-gold/30 overflow-hidden group hover:border-dark-crimson transition-all duration-500 hover:shadow-2xl hover:-translate-y-2">
              <div
                className="relative h-96 overflow-hidden bg-ivory flex items-center justify-center px-10 py-8 cursor-zoom-in"
                onClick={() => setLightbox({ image: item.image, title: item.title, size: item.size })}
                data-testid={`gallery-image-${item.id}`}
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className={`max-w-full max-h-full object-contain transition-opacity duration-700 ${item.imageContext ? 'group-hover:opacity-0' : ''}`}
                />
                {item.imageContext && (
                  <img
                    src={item.imageContext}
                    alt={`${item.title} in gallery`}
                    className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                  />
                )}
              </div>
              <CardContent className="p-8 bg-ivory">
                <Link
                  to={`/piece/gallery/${slugify(`${item.title} ${item.artist || ''}`)}`}
                  data-testid={`gallery-detail-${item.id}`}
                  className="block hover:text-dark-crimson transition-colors"
                >
                  <h3 className="text-2xl font-bold text-dark-green mb-2 hover:text-dark-crimson transition-colors" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {localize(item, 'title', lang)}
                  </h3>
                </Link>
                <Link
                  to={`/artists/${slugify(item.artist)}`}
                  className="text-dark-crimson mb-4 font-semibold inline-block hover:underline hover:text-crimson transition-colors"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  data-testid={`gallery-artist-link-${item.id}`}
                >
                  {item.artist}
                </Link>
                <p className="text-charcoal mb-6 text-sm leading-relaxed" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{localize(item, 'description', lang)}</p>
                <div className="flex justify-between items-center pt-6 border-t-2 border-gold/30">
                  <span className="text-3xl font-bold text-gold">{item.price}</span>
                  <div className="flex items-center gap-2">
                    <ShareMenu
                      variant="icon"
                      url={`${window.location.origin}/api/share/gallery/${slugify(`${item.title} ${item.artist || ''}`)}`}
                      title={`${item.title} — ${item.artist}`}
                    />
                    <Link
                      to={`/contact?subject=${encodeURIComponent(`${t('common.inquireSubject')} ${item.title} — ${item.artist}`)}`}
                      data-testid={`gallery-inquire-${item.id}`}
                    >
                      <Button className="bg-dark-crimson hover:bg-crimson text-cream rounded-none transition-colors border border-gold px-6 py-3">
                        {t('common.inquire')}
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredItems.length === 0 && (
          <div className="text-center py-20">
            <p className="text-charcoal/60 text-xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{t('common.noResults')}</p>
          </div>
        )}
      </div>

      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          data-testid="lightbox-overlay"
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6 cursor-zoom-out"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            data-testid="lightbox-close"
            aria-label="Close"
            className="absolute top-6 right-6 text-cream hover:text-gold transition-colors p-2"
          >
            <X size={32} />
          </button>
          <div className="max-w-[92vw] max-h-[88vh] flex flex-col items-center gap-4">
            <img
              src={lightbox.image}
              alt={lightbox.title}
              onClick={(e) => e.stopPropagation()}
              className="max-w-full max-h-[80vh] object-contain shadow-[0_30px_60px_-15px_rgba(0,0,0,0.6)]"
            />
            <div className="text-center text-cream" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <div className="text-2xl tracking-wide" style={{ fontFamily: 'Playfair Display, serif' }}>
                {lightbox.title}
              </div>
              {lightbox.size && (
                <div className="text-gold/80 text-sm mt-1">{lightbox.size}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
